import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect } from 'react';

import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { OtpField } from '@/components/forms';
import { QR_BACKGROUND, QR_FOREGROUND, QR_QUIET_ZONE_MODULES } from '@/features/identity/constants/qr';
import { useMfaSetup } from '@/features/identity/hooks/useMfaSetup';
import { useToast } from '@/hooks/useToast';

export interface EnableMfaDialogProps {
  open: boolean;
  onClose: () => void;
  onEnabled: () => void;
}

/**
 * Two-stage MFA enrollment (ADR-0006): opening the dialog creates a PENDING enrollment (`/mfa/setup`)
 * and shows the QR/secret, but MFA is NOT enabled until the user proves possession by entering a valid
 * TOTP code (`/mfa/verify`). Cancelling — or closing the dialog — discards the pending enrollment
 * (`/mfa/cancel`), and any pending enrollment expires server-side regardless, so a user can never get
 * locked out by closing the dialog without scanning the QR.
 */
export function EnableMfaDialog({ open, onClose, onEnabled }: EnableMfaDialogProps) {
  const { form, setup, verify, cancel, reset, result, isSettingUp, setupError, verifyError } =
    useMfaSetup();
  const { showSuccess } = useToast();
  const { formState } = form;

  useEffect(() => {
    if (open && !result && !isSettingUp && !setupError) {
      void setup();
    }
  }, [open, result, isSettingUp, setupError, setup]);

  const close = () => {
    reset();
    onClose();
  };

  const handleCancel = async () => {
    await cancel();
    close();
  };

  const onSubmit = form.handleSubmit(async ({ code }) => {
    if (await verify(code)) {
      showSuccess('Multi-factor authentication is now enabled.');
      onEnabled();
      close();
    }
  });

  const handleCopySecret = () => {
    if (result) void navigator.clipboard.writeText(result.secret);
  };

  return (
    <Dialog open={open} onClose={() => void handleCancel()} maxWidth="xs" fullWidth>
      <DialogTitle>Enable multi-factor authentication</DialogTitle>
      <Stack component="form" onSubmit={onSubmit} noValidate>
        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
            {isSettingUp ? <LoadingSpinner label="Setting up MFA…" /> : null}
            {setupError ? (
              <Alert severity="error" sx={{ width: '100%' }}>
                {setupError}
              </Alert>
            ) : null}
            {result ? (
              <>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy,
                  1Password…).
                </Typography>
                <QRCodeSVG
                  value={result.provisioningUri}
                  size={180}
                  marginSize={QR_QUIET_ZONE_MODULES}
                  bgColor={QR_BACKGROUND}
                  fgColor={QR_FOREGROUND}
                />
                <Typography variant="caption" color="text.secondary">
                  Or enter this code manually:
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <TextField
                    value={result.secret}
                    slotProps={{ input: { readOnly: true } }}
                    fullWidth
                    size="small"
                  />
                  <Tooltip title="Copy secret">
                    <IconButton onClick={handleCopySecret} aria-label="Copy secret">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  Enter the 6-digit code from your app to finish enabling MFA:
                </Typography>
                {verifyError ? (
                  <Alert severity="error" sx={{ width: '100%' }}>
                    {verifyError}
                  </Alert>
                ) : null}
                <OtpField name="code" control={form.control} />
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => void handleCancel()} disabled={formState.isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!result || formState.isSubmitting}
            startIcon={
              formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            Verify &amp; Enable MFA
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
}
